import { create } from 'zustand';
import { useNotificationStore } from '../notificationStore';
import { ApiUtils, AuthUtils } from '@shared/utils';
import { AuthApi, RequestStrategyEnum, type LoginRequest, type LoginResponse, type TwoFactorRequest } from '@shared/api';
import { Constants } from '@shared/widgets';

interface LoginState {
  data: LoginResponse | null;
  error: string | null;
  success: boolean | null;
  loading: boolean;
  login: (request: LoginRequest, signal?: AbortSignal) => Promise<void>;
  twoFactor: (request: TwoFactorRequest, signal?: AbortSignal) => Promise<void>;
  clear: () => void;
}

export const useLoginStore = create<LoginState>((set) => ({
  loading: false,
  data: null,
  success: false,
  error: null,

  login: async (request: LoginRequest, signal?: AbortSignal) => {
    try {
      set({ loading: true, success: null, error: null });
      const response = await AuthApi.login(request, signal);
      if (response.status === 'allow') {
        AuthUtils.login(response);
        set({ success: true, data: response, loading: false });
      } else if (response.status === 'auth') set({ success: true, data: response, loading: false });
      else set({ success: false, error: 'Login failed', loading: false });
    } catch (error) {
      set({ success: null, data: null, error: ApiUtils.getApiError(error), loading: false });
      useNotificationStore.getState().setError(Constants.LOGIN_ERROR_MESSAGE);
    }
  },

  twoFactor: ApiUtils.wrapperApi(async (request: TwoFactorRequest, signal?: AbortSignal) => {
    try {
      set({ loading: true, success: null, error: null });
      const response = await AuthApi.twoFactor(request, signal);
      if (response.status === 'allow') {
        AuthUtils.login(response);
        set({ success: true, data: response, loading: false });
      } else if (response.status === 'locked_out') {
        set({ success: true, data: response, loading: false });
      } else {
        set({ success: false, error: 'Login failed', loading: false });
      }
    } catch (error) {
      set({ success: null, data: null, error: ApiUtils.getApiError(error), loading: false });
      useNotificationStore.getState().setError(Constants.LOGIN_ERROR_MESSAGE);
    }
  }, RequestStrategyEnum.Debounce),

  clear: () => set({ data: null, error: null, success: false, loading: false }),
}));
